import { memo } from "react";
import { Edit, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import { CommissionTypeBadge } from "./CommissionTypeBadge";
import type { CommissionResponseDTO } from "../types/commission.types";

interface CommissionsTableProps {
  commissions: CommissionResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (commission: CommissionResponseDTO) => void;
  onDelete: (commission: CommissionResponseDTO) => void;
  onPageChange: (page: number) => void;
}

function formatOrigin(commission: CommissionResponseDTO): string {
  if (commission.graduationId !== null) {
    return `Formatura #${commission.graduationId}`;
  }
  if (commission.rentalId !== null) {
    return `Aluguel #${commission.rentalId}`;
  }
  return "—";
}

/**
 * Tabela paginada das comissões individuais de um vendedor (modal).
 *
 * Coluna "Origem" exibe texto estático ("Aluguel #X" ou "Formatura #Y").
 * Paginação server-side com índice 0-based. Mantém o padrão visual da
 * `InvoicesTable`.
 *
 * @example
 * <CommissionsTable
 *   commissions={list.data}
 *   loading={list.loading}
 *   currentPage={list.page}
 *   totalPages={list.totalPages}
 *   onEdit={openEditDialog}
 *   onDelete={openDeleteDialog}
 *   onPageChange={list.setPage}
 * />
 */
export const CommissionsTable = memo(function CommissionsTable({
  commissions,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
}: CommissionsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Cálculo</TableHead>
              <TableHead>Origem (data)</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {commissions.map((commission) => (
              <TableRow key={commission.id}>
                <TableCell className="font-medium">{commission.id}</TableCell>
                <TableCell>
                  <CommissionTypeBadge type={commission.rateType} />
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(commission.value)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatOrigin(commission)}
                </TableCell>
                <TableCell>{formatDateTime(commission.calculatedAt)}</TableCell>
                <TableCell>{formatDateTime(commission.rentedAt)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Can permission={ACTIONS.commissions.update}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onEdit(commission)}
                        title="Editar"
                        aria-label="Editar comissão"
                        disabled={loading}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Can>
                    <Can permission={ACTIONS.commissions.delete}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onDelete(commission)}
                        title="Excluir"
                        aria-label="Excluir comissão"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </Can>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
